import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { getAuthToken } from '../../utils/auth';
import {
  ExternalLink, Copy, Send, Loader2, Plus, ChevronDown, ChevronUp,
  AlertTriangle, ArrowLeft, Hammer, Sparkles, Check,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

const STATUS_NEXT = { found: 'contacted', contacted: 'replied', replied: 'hired', hired: 'passed', passed: 'found' };
const STATUS_STYLE = {
  found: 'bg-zinc-500/15 text-zinc-300', contacted: 'bg-blue-500/15 text-blue-300',
  replied: 'bg-amber-500/15 text-amber-300', hired: 'bg-emerald-500/15 text-emerald-300',
  passed: 'bg-red-500/15 text-red-300',
};

const copyText = async (text, msg) => {
  try {
    await navigator.clipboard.writeText(text || '');
    toast.success(msg);
  } catch (e) { toast.error('Copy failed'); }
};

// One provider block: LEE's pick (hero) or a team row
const ProviderCard = ({ item, hero, projectId, onLogged }) => {
  const [logOpen, setLogOpen] = useState(false);
  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  const [note, setNote] = useState('');
  const primary = (item.links || [])[0];
  const alts = (item.links || []).slice(1, 4);

  const logPick = async () => {
    if (!name.trim()) { toast.error('Who did you message? Add their name'); return; }
    try {
      await axios.post(`${API_URL}/api/idea/projects/${projectId}/builders/candidates`,
        { name, link, note, role: item.provider, platform: primary?.name || '', status: 'contacted' }, auth());
      toast.success(`${name} logged — LEE will keep track`);
      setLogOpen(false); setName(''); setLink(''); setNote('');
      onLogged();
    } catch (e) { toast.error('Could not save'); }
  };

  return (
    <div className={`rounded-2xl border p-4 ${hero ? 'border-amber-500/40 bg-amber-500/[0.06]' : 'border-white/10 bg-white/[0.03]'}`}
      data-testid={hero ? 'lee-pick-card' : 'team-card'}>
      {hero && (
        <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1.5 mb-1.5">
          <Sparkles className="w-3 h-3" /> LEE's pick — start here
        </p>
      )}
      <p className={`font-bold text-zinc-100 ${hero ? 'text-lg' : 'text-[15px]'}`}>{item.provider}</p>
      <p className="text-[13px] text-zinc-400 mt-1 leading-snug">{item.why}</p>
      {item.est_budget_note && <p className="text-[11px] text-zinc-600 mt-1.5 italic">{item.est_budget_note}</p>}

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {primary && (
          <a href={primary.url} target="_blank" rel="noreferrer"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold transition-colors ${hero ? 'bg-amber-500 text-black hover:bg-amber-400' : 'bg-white/[0.08] text-zinc-200 hover:bg-white/[0.14]'}`}
            data-testid="provider-primary-link">
            See real candidates on {primary.name} <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        {alts.map((l) => (
          <a key={l.name} href={l.url} target="_blank" rel="noreferrer"
            className="text-[11px] text-zinc-500 hover:text-amber-300 transition-colors flex items-center gap-1"
            data-testid="provider-alt-link">
            or {l.name} <ExternalLink className="w-2.5 h-2.5" />
          </a>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-3">
        {item.ready_message && (
          <button onClick={() => copyText(item.ready_message, 'Message copied — paste it to the candidate')}
            className="flex items-center gap-1.5 text-[12px] font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
            data-testid="copy-message-btn">
            <Copy className="w-3.5 h-3.5" /> Copy your intro message
          </button>
        )}
        <button onClick={() => setLogOpen(!logOpen)}
          className="flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors"
          data-testid="log-pick-btn">
          <Check className="w-3.5 h-3.5" /> I messaged someone
        </button>
      </div>

      {logOpen && (
        <div className="mt-3 rounded-xl bg-black/30 border border-white/[0.07] p-3 space-y-2" data-testid="log-pick-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Their name *"
              className="bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-[12px] text-white placeholder-zinc-600 min-w-0"
              data-testid="log-name-input" />
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Profile link (optional)"
              className="bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-[12px] text-white placeholder-zinc-600 min-w-0"
              data-testid="log-link-input" />
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            placeholder="Paste their rate, reviews, bio — helps LEE pick the best later"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-[12px] text-white placeholder-zinc-600 resize-none"
            data-testid="log-note-input" />
          <button onClick={logPick} className="px-3 py-1.5 rounded-lg bg-amber-500 text-black text-[12px] font-bold" data-testid="log-save-btn">
            Save
          </button>
        </div>
      )}
    </div>
  );
};

const OutreachRow = ({ projectId, item, site, onDone }) => {
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);
  const approve = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient)) {
      toast.error("Enter the firm's real intake email before approving");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/idea/projects/${projectId}/builders/outreach/${item.email_id}/approve`,
        { recipient }, auth());
      const st = r.data?.send_result?.status;
      if (st === 'sent') toast.success(`Outreach sent to ${item.firm}`);
      else if (st === 'queued') toast(`Queued for ${item.firm}`);
      else toast.error(`Could not send: ${st || 'unknown'}`);
      onDone(item.email_id, st);
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
    setSending(false);
  };
  if (item.status !== 'draft') {
    return (
      <div className="rounded-xl bg-black/30 border border-emerald-500/20 p-3 flex items-center gap-2" data-testid="outreach-sent-row">
        <p className="text-[13px] text-zinc-300 truncate">{item.firm} — <span className="text-emerald-400 font-bold">{item.status}</span></p>
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-black/30 border border-white/[0.07] p-3" data-testid="outreach-row">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13px] font-semibold text-zinc-200">{item.firm}</p>
        {item.preview && (
          <button onClick={() => setPreview(!preview)} className="text-[11px] text-zinc-500 hover:text-amber-300 transition-colors font-bold" data-testid="outreach-preview-btn">
            {preview ? 'Hide email' : 'Read email'}
          </button>
        )}
      </div>
      <p className="text-[11px] text-zinc-500 truncate mt-0.5">{item.subject}</p>
      {preview && (
        <pre className="mt-2 text-[11px] text-zinc-400 whitespace-pre-wrap font-sans leading-relaxed max-h-48 overflow-y-auto bg-black/30 rounded-lg p-2.5" data-testid="outreach-preview-text">{item.preview}</pre>
      )}
      <div className="flex items-center gap-2 mt-2">
        <input value={recipient} onChange={(e) => setRecipient(e.target.value)}
          placeholder="firm intake email (check their site)"
          className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-[12px] text-white placeholder-zinc-600"
          data-testid="outreach-recipient-input" />
        <button onClick={approve} disabled={sending}
          className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-lg bg-emerald-500 text-black text-[12px] font-bold disabled:opacity-50 hover:bg-emerald-400 transition-colors"
          data-testid="outreach-approve-btn">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Approve & send
        </button>
      </div>
      {site && (
        <a href={site} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-amber-300 transition-colors mt-1.5"
          data-testid="outreach-site-link">
          Find their intake email on {site.replace('https://', '').replace('www.', '').split('/')[0]} <ExternalLink className="w-2.5 h-2.5" />
        </a>
      )}
    </div>
  );
};

export default function BuilderHub() {
  const { projectId } = useParams();
  const [plan, setPlan] = useState(null);
  const [project, setProject] = useState(null);
  const [starting, setStarting] = useState(false);
  const [location, setLocation] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [ranking, setRanking] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/idea/projects/${projectId}/builders`, auth())
      .then((r) => setPlan(r.data)).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    load();
    axios.get(`${API_URL}/api/idea/projects/${projectId}`, auth())
      .then((r) => setProject(r.data.project || r.data)).catch(() => {});
  }, [projectId, load]);

  useEffect(() => {
    if (plan?.status === 'running' && !pollRef.current) {
      pollRef.current = setInterval(load, 5000);
    }
    if (plan?.status !== 'running' && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [plan, load]);

  const start = async () => {
    setStarting(true);
    try {
      await axios.post(`${API_URL}/api/idea/projects/${projectId}/builders/run`,
        { location: location.trim() || null }, auth());
      toast.success('LEE is picking the best route (30–60s)');
      load();
    } catch (e) { toast.error('Could not start'); }
    setStarting(false);
  };

  const cycleStatus = async (c) => {
    const next = STATUS_NEXT[c.status] || 'found';
    try {
      await axios.patch(
        `${API_URL}/api/idea/projects/${projectId}/builders/candidates/${c.id}`,
        { status: next }, auth());
      load();
    } catch (e) { toast.error('Could not update'); }
  };

  const rankNow = async () => {
    setRanking(true);
    try {
      await axios.post(`${API_URL}/api/idea/projects/${projectId}/builders/rank`, {}, auth());
      toast.success("LEE made his pick");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not rank'); }
    setRanking(false);
  };

  const onOutreachDone = (emailId, status) => {
    setPlan((p) => ({
      ...p,
      outreach: (p.outreach || []).map((o) =>
        o.email_id === emailId ? { ...o, status: status || 'sent' } : o),
    }));
  };

  const stale = plan?.status === 'ready' && !plan.top_pick;
  const showEmpty = !plan || plan.status === 'none' || stale;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-5 pb-10" data-testid="builder-hub-page">
        <div>
          <Link to="/inventions" className="text-[12px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1 mb-2" data-testid="builders-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Ideas
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2.5">
            <Hammer className="w-6 h-6 text-amber-400" /> Bring it to life
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{project?.title || '…'}</p>
        </div>

        {showEmpty && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center space-y-4" data-testid="builders-empty-state">
            <Sparkles className="w-8 h-8 text-amber-400 mx-auto" />
            <p className="text-[15px] text-zinc-200 font-semibold">LEE picks the best person to build this</p>
            <p className="text-[13px] text-zinc-500 max-w-md mx-auto">
              One clear pick with real candidates one tap away — plus a ready-to-send message so you don't have to write anything.
            </p>
            <input value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="City (only for location ideas — restaurants, shops)"
              className="w-full max-w-xs mx-auto block bg-black/40 border border-white/10 rounded-full px-4 py-2 text-[13px] text-white placeholder-zinc-600 text-center"
              data-testid="builders-location-input" />
            <button onClick={start} disabled={starting}
              className="px-6 py-2.5 rounded-full bg-amber-500 text-black text-[14px] font-bold disabled:opacity-50 hover:bg-amber-400 transition-colors"
              data-testid="builders-run-btn">
              {starting ? 'Starting…' : stale ? 'Rebuild my plan' : 'Do it, LEE'}
            </button>
          </div>
        )}

        {plan?.status === 'running' && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center" data-testid="builders-running">
            <Loader2 className="w-6 h-6 text-amber-400 mx-auto animate-spin mb-2" />
            <p className="text-[14px] text-zinc-300">LEE is picking your best route…</p>
          </div>
        )}

        {plan?.status === 'error' && (
          <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.05] p-4 flex items-center gap-2" data-testid="builders-error">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <p className="text-[13px] text-red-300">{plan.error}</p>
            <button onClick={start} className="ml-auto text-[12px] font-bold text-zinc-300 underline">Retry</button>
          </div>
        )}

        {plan?.status === 'ready' && plan.top_pick && (
          <>
            {plan.strategy && (
              <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.05] p-4" data-testid="strategy-banner">
                <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">LEE's build strategy</p>
                <p className="text-[15px] font-bold text-zinc-100">
                  {plan.strategy.headline || (
                    plan.strategy.mode === 'turnkey' ? 'One builder, A to Z'
                      : plan.strategy.mode === 'kit_and_specialist' ? 'Order the kit, ship it to your hire'
                        : 'No hire needed — LEE can drive this')}
                </p>
                <p className="text-[13px] text-zinc-400 mt-1 leading-snug">{plan.strategy.explanation}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2.5">
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/[0.06] text-zinc-300" data-testid="parts-plan-chip">
                    {plan.strategy.parts_plan === 'hire_sources' && 'Parts: your hire sources them — list handed over'}
                    {plan.strategy.parts_plan === 'owner_ships' && 'Parts: LEE\'s list gets ordered & shipped to your hire'}
                    {plan.strategy.parts_plan === 'no_parts' && 'Parts: none needed'}
                  </span>
                  {plan.parts_handoff && plan.strategy.parts_plan !== 'no_parts' && (
                    <button onClick={() => copyText(plan.parts_handoff, 'Parts list copied — hand it to your hire')}
                      className="flex items-center gap-1 text-[11px] font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
                      data-testid="copy-parts-handoff-btn">
                      <Copy className="w-3 h-3" /> Copy parts list
                    </button>
                  )}
                </div>
              </div>
            )}

            <ProviderCard item={plan.top_pick} hero projectId={projectId} onLogged={load} />

            {(plan.team || []).length > 0 && (
              <div className="space-y-2.5">
                <p className="text-[11px] uppercase tracking-widest text-zinc-600 font-bold">You'll also need</p>
                {plan.team.map((t, i) => (
                  <ProviderCard key={i} item={t} projectId={projectId} onLogged={load} />
                ))}
              </div>
            )}

            {(plan.candidates || []).length > 0 && (
              <div className="space-y-2" data-testid="candidates-section">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] uppercase tracking-widest text-zinc-600 font-bold">Your people</p>
                  {(plan.candidates || []).filter((c) => c.status !== 'passed').length >= 2 && (
                    <button onClick={rankNow} disabled={ranking}
                      className="flex items-center gap-1.5 text-[12px] font-bold text-amber-400 hover:text-amber-300 transition-colors disabled:opacity-50"
                      data-testid="rank-candidates-btn">
                      {ranking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      LEE, who's best?
                    </button>
                  )}
                </div>
                {plan.candidate_ranking && (
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3" data-testid="ranking-verdict">
                    <p className="text-[12px] font-bold text-amber-300 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      LEE's pick: {(plan.candidates || []).find((c) => c.id === plan.candidate_ranking.recommended_id)?.name || '—'}
                    </p>
                    <p className="text-[12px] text-zinc-400 mt-1 leading-snug">{plan.candidate_ranking.reasoning}</p>
                  </div>
                )}
                {plan.candidates.map((c) => {
                  const verdict = (plan.candidate_ranking?.ranking || []).find((r) => r.id === c.id)?.verdict;
                  const recommended = plan.candidate_ranking?.recommended_id === c.id;
                  return (
                    <div key={c.id}
                      className={`rounded-xl border p-3 ${recommended ? 'border-amber-500/40 bg-amber-500/[0.04]' : 'border-white/[0.07] bg-black/30'}`}
                      data-testid="candidate-row">
                      <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-zinc-200 truncate">
                            {c.link ? <a href={c.link} target="_blank" rel="noreferrer" className="hover:text-amber-300">{c.name} ↗</a> : c.name}
                            {recommended && <span className="text-[10px] text-amber-400 font-bold ml-1.5">★ LEE's pick</span>}
                          </p>
                          <p className="text-[11px] text-zinc-500 truncate">{[c.role, c.platform, c.rate].filter(Boolean).join(' · ')}</p>
                        </div>
                        <button onClick={() => cycleStatus(c)}
                          className={`text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 ${STATUS_STYLE[c.status] || STATUS_STYLE.found}`}
                          title="Tap to advance status" data-testid="candidate-status-btn">
                          {c.status}
                        </button>
                      </div>
                      {verdict && <p className="text-[11px] text-zinc-500 mt-1.5 leading-snug italic">{verdict}</p>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Everything else tucked away */}
            <button onClick={() => setMoreOpen(!moreOpen)}
              className="w-full flex items-center justify-center gap-1.5 text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors py-1"
              data-testid="more-tools-btn">
              {moreOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {moreOpen ? 'Hide extra tools' : 'More tools (full brief, firm outreach)'}
            </button>

            {moreOpen && (
              <div className="space-y-4" data-testid="more-tools-panel">
                <button onClick={() => copyText(plan.brief, 'Full brief copied — paste it into any job post')}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] py-3 text-[13px] font-bold text-zinc-200 flex items-center justify-center gap-2 hover:border-amber-500/30 transition-colors"
                  data-testid="brief-copy-btn">
                  <Copy className="w-4 h-4 text-amber-400" /> Copy the full project brief
                </button>
                {(plan.outreach || []).length > 0 && (
                  <div className="space-y-2" data-testid="outreach-section">
                    <p className="text-[11px] uppercase tracking-widest text-zinc-600 font-bold">Outreach to build firms — approve to send</p>
                    {plan.outreach.map((o) => (
                      <OutreachRow key={o.email_id} projectId={projectId} item={o}
                        site={(plan.firms || []).find((f) => f.name === o.firm)?.site}
                        onDone={onOutreachDone} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
